import type { ExternalTableSchema } from '@packages/table-builder';
import { transferTaxCertColumn } from './transferTaxCertColumn';

type TableColumn = NonNullable<ExternalTableSchema['columns']>[number];

/**
 * Legacy runtime — View Details + Included (shared DTO):
 *
 * - Runtime: schemaData.columns → TableCell when server `showDecimal: true`
 * - `TableSettings.tsx` L1040–1051: convertNumberWithCommas alone → `0.0000` for numeric zero
 * - Dead `transferDetailsHeader` L92–94: `(value as boolean) ? toFixed(4) : '0'` → zero shows `0`
 * - **`CommaFormatted`** (`formatters.ts` L8–12): `hasDecimal ? toFixed(4) : toLocaleString()`
 *   matches UAT: `0`, `112.0400`, `122.2000`, `36,722` (not `0.0000` on zero)
 * - V2: `format.decimals + fixed` then `format.commas` → CommaFormatted second pass (cell-renderers L203–205)
 */
export const legacyShowDecimalColumn = (
  field: string,
  header: string,
  opts: { sortable?: boolean; editable?: boolean; validation?: TableColumn['validation'] } = {},
): TableColumn => ({
  field,
  header,
  type: 'text',
  sortable: opts.sortable ?? false,
  width: 200,
  align: 'center',
  format: { decimals: 4, fixed: true, commas: true },
  fields: { formatAsNumber: 'true' },
  emptyDisplay: '-',
  ...(opts.editable ? { editable: true, validation: opts.validation } : {}),
});

export const transferParticipantDetailColumn: TableColumn = {
  field: 'participantDetail',
  header: 'Participant Details',
  sortable: true,
  width: 200,
};

export const transferTransactionDateColumn: TableColumn = {
  field: 'transactionDate',
  header: 'Transaction Date',
  type: 'date',
  sortable: true,
  width: 200,
  emptyDisplay: '-',
};

export const transferRecordTypeColumn: TableColumn = {
  field: 'recordType',
  header: 'Record Type',
  sortable: true,
  width: 200,
};

/** Server showDecimal:true → TableCell 4dp (UAT: 112.0400 / 122.2000). */
export const transferTransactionValuesColumn: TableColumn = legacyShowDecimalColumn(
  'transactionValues',
  'Transaction Value $',
  { sortable: true },
);

export const transferFmvColumn: TableColumn = legacyShowDecimalColumn('fmv', 'FMV $', {
  sortable: true,
});

/** Server isNumberField:true, showDecimal:false → NumberField Tag + comma integer (UAT: 0). */
export const transferVestingUnitColumn: TableColumn = {
  field: 'vestingUnit',
  header: 'Unit',
  type: 'number',
  sortable: true,
  width: 200,
  align: 'center',
  format: { commas: true },
  emptyDisplay: '-',
};

export const transferGrantNameColumn: TableColumn = {
  field: 'grantName',
  header: 'Grant Name',
  sortable: true,
  width: 200,
};

export const transferAwardNumberColumn: TableColumn = {
  field: 'awardNumber',
  header: 'Award ID',
  sortable: true,
  width: 200,
  emptyDisplay: '-',
};

export const transferAwardTypeColumn: TableColumn = {
  field: 'awardType',
  header: 'Award Type',
  type: 'badge',
  sortable: true,
  width: 200,
  align: 'center',
  badge: {
    statusMap: { RSU: 'success', PSU: 'pink-tag' },
    defaultVariant: 'default',
  },
};

export const transferSettlementTypeColumn: TableColumn = {
  field: 'settlementType',
  header: 'Settlement Type',
  sortable: true,
  width: 200,
};

export const transferFundingStatusColumn: TableColumn = {
  field: 'fundingStatus',
  header: 'Funding Status',
  sortable: true,
  width: 200,
};

export const transferTransactionContextColumn: TableColumn = {
  field: 'transctionContext',
  header: 'Transaction Context',
  sortable: true,
  width: 300,
};

/** View Details tab — `viewDetailsSchema.ts`. */
export const transferItemCoreColumns: TableColumn[] = [
  transferParticipantDetailColumn,
  transferTransactionDateColumn,
  transferRecordTypeColumn,
  transferTransactionValuesColumn,
  transferFmvColumn,
  transferVestingUnitColumn,
  transferGrantNameColumn,
  transferAwardNumberColumn,
  transferAwardTypeColumn,
  transferTaxCertColumn,
  transferSettlementTypeColumn,
  transferFundingStatusColumn,
  transferTransactionContextColumn,
];
